import Checkboxes from 'components/Form/Checkboxes';
import InputHidden from 'components/Form/InputHidden';
import InputIncremental from 'components/Form/InputIncremental';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import I18nImage from 'components/I18nImage';
import Stacked from 'components/Layout/Stacked';
import {H3} from 'components/Typography';
import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {c, jt, t} from 'ttag';
import {FormFields} from 'types/utils';
import getPreferredLanguage from 'utils/locale';

import enterpriseEN from '../assets/images/enterprise_en.png';
import enterpriseJA from '../assets/images/enterprise_ja.png';
import PlanChangeFooter from '../footer';
import BillingChangeSubscriptionHeader from '../Header';
import parseChangeSubscriptionFormData from '../parse-change-subscription-form-data';
import PriceCalculation from '../PriceCalculation';
import {
    getAdditionalOptions,
    getIntergrationOptions,
    getTermOptionRadio,
} from '../selectableOptions';
import TabNavigation from '../TabNavigation';
import {
    BillingPlanIntegrationsPriceList,
    BillingPlanOption,
    BillingPlanOptionsPriceList,
    BillingScreenName,
    ParsedSearchParams,
    Term,
    TermOptions,
} from '../types';
import useGetEnterprisePrices from './services';

type PageEnterpriseFormData = FormFields<
    {[K in BillingPlanOption]: boolean} & {
        billingFrequency: Term;
        plan_code: 'enterprise';
        sendingorders: number;
    }
>;

type PageEnterprisePublicProps = {
    integrationsPrices: BillingPlanIntegrationsPriceList;
    optionPrices: BillingPlanOptionsPriceList;
    sendingOrderCount: number;
    termOptions: TermOptions;
};

type PageEnterprisePrivateProps = {
    isAuthenticated: boolean;
    onSuccess: (url: string) => void;
    onTabChange: (tag: BillingScreenName) => void;
};

const PageEnterprise: FC<
    PageEnterprisePrivateProps & PageEnterprisePublicProps
> = ({
    integrationsPrices,
    isAuthenticated,
    onSuccess,
    onTabChange,
    optionPrices,
    sendingOrderCount,
    termOptions,
}) => {
    const methods = useForm<PageEnterpriseFormData>({
        defaultValues: {
            billingFrequency: 'monthly',
            sendingorders: 300,
        },
        shouldUnregister: true,
    });

    const {getValues, handleSubmit, watch} = methods;

    const [searchParams, setSearchParams] = useState<ParsedSearchParams>(() =>
        parseChangeSubscriptionFormData(getValues()),
    );
    const {data, isError, isLoading, isSuccess} = useGetEnterprisePrices({
        onError: () => {},
        searchParams,
    });

    watch((formData) => {
        setSearchParams(parseChangeSubscriptionFormData(formData));
    });

    const language = getPreferredLanguage();

    const termOptionRadio = getTermOptionRadio(termOptions);
    const hasTermHelpText = termOptions.some((option) => option.disabled);

    const additionlOptions = getAdditionalOptions(optionPrices);

    const integrationsOptions = getIntergrationOptions(integrationsPrices);

    const onSubmit = (formData: PageEnterpriseFormData) => {
        onSuccess(
            `/billing/plan/checkout/?${new URLSearchParams(
                parseChangeSubscriptionFormData(formData),
            ).toString()}`,
        );
    };

    // LINK HREF NOT IMPLEMENTED
    const findOutMoreLink = (
        <a className="text-xs" href="/">
            {t`(Find out more)`}
        </a>
    );

    return (
        <div>
            <BillingChangeSubscriptionHeader
                isAuthenticated={isAuthenticated}
            />
            <TabNavigation activeTab="enterprise" onTabChange={onTabChange} />
            <div className="grid w-max max-w-full grid-cols-2 bg-green-10">
                <div className="flex w-full flex-col items-center p-8">
                    <I18nImage
                        className="max-h-48 max-w-48"
                        src={{
                            en: enterpriseEN,
                            ja: enterpriseJA,
                        }}
                    />
                    <div className="my-4 text-center">
                        {t`Affordable pricing plan with no limits to the number of team members and clients. Extend advanced invoicing operations to your tens of thousands clients effectively with MakeLeaps for Enterprise.`}
                    </div>
                    <a
                        href={`https://www.makeleaps.com/${language}/pricing-detail`}
                    >
                        {t`Find out more`}
                    </a>
                </div>
                <div className="w-full">
                    <FormProvider {...methods}>
                        <form className="p-4" onSubmit={handleSubmit(onSubmit)}>
                            <InputHidden name="plan_code" value="enterprise" />
                            <Stacked className="w-full">
                                <Stacked className="bg-green-50 p-4">
                                    <div>
                                        <H3 className="mt-0">
                                            {c('Billing').t`Team Members`}
                                        </H3>
                                        <p>{t`Unlimited`}</p>
                                    </div>
                                    <div>
                                        <H3 className="mt-0">
                                            {t`Active Clients`}
                                        </H3>
                                        <p>{t`Unlimited`}</p>
                                    </div>
                                    <div>
                                        <H3 className="mt-0">
                                            {t`Sending Orders`}
                                        </H3>
                                        <p>
                                            {t`300 free sending orders monthly, then 30 yen/order beyond that. In addition to the sending order fee, you will need MailPoints to send documents via Japan Post. To avoid unexpected charges, please set a limit below. Sending limits can be adjusted at any time.`}
                                        </p>
                                    </div>
                                    <div>
                                        <InputIncremental
                                            label={t`Sending Order Limit (${sendingOrderCount} sent already this month)`}
                                            max={100_000_000}
                                            min={300}
                                            name="sendingorders"
                                            required={true}
                                            requiredType="asterisk"
                                            step={5}
                                        />
                                    </div>
                                </Stacked>
                                <div>
                                    <div>{jt`Options ${findOutMoreLink}`}</div>
                                    <Checkboxes options={additionlOptions} />
                                </div>
                                <div>
                                    <div>{t`Integrations`}</div>
                                    <Checkboxes options={integrationsOptions} />
                                </div>
                                <Radio
                                    helpText={
                                        hasTermHelpText
                                            ? t`(This plan only supports monthly payment)`
                                            : undefined
                                    }
                                    label={t`Billing Frequency`}
                                    name="billingFrequency"
                                    options={termOptionRadio}
                                    required={true}
                                    requiredType="asterisk"
                                />
                                <div className="grid rounded bg-green-200">
                                    <PriceCalculation
                                        isError={isError}
                                        isLoading={isLoading}
                                        isSuccess={isSuccess}
                                        value={data?.amount}
                                    />
                                    <SubmitButton disabled={isError}>
                                        {t`Subscribe`}
                                    </SubmitButton>
                                </div>
                            </Stacked>
                        </form>
                    </FormProvider>
                </div>
            </div>
            <PlanChangeFooter />
        </div>
    );
};

export default PageEnterprise;
